/** Reseller accounts management page - downstream accounts only. */
import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resellerFetch } from "../../api/client";
import ImportAccountsDialog from "../../components/accounts/ImportAccountsDialog";
import { Upload as UploadIcon } from "@mui/icons-material";
import { useNotify } from "../../components/common/NotificationProvider";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as TransferBalanceIcon,
  Description as ViewCdrsIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";

/** API base URL. */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/** Account interface. */
interface Account {
  id: string;
  tenant: string;
  account: string;
  account_type: string;
  balance: number;
  credit_limit: number | null;
  disabled: boolean;
  level: number | null;
  margin_percent: number | null;
  margin_floor: number | null;
  full_account_path: string;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<Account>();

/** Reseller level chip component. */
function LevelChip({ level }: { level: number | null }) {
  const getLevelColor = (lvl: number | null) => {
    if (lvl === null) return "default";
    if (lvl === 1) return "primary";
    if (lvl === 2) return "secondary";
    if (lvl === 3) return "info";
    return "default";
  };

  const getLevelLabel = (lvl: number | null) => {
    if (lvl === null) return "Root";
    return `L${lvl}`;
  };

  return (
    <Chip
      label={getLevelLabel(level)}
      color={getLevelColor(level)}
      size="small"
    />
  );
}

/** Status chip component. */
function StatusChip({ disabled }: { disabled: boolean }) {
  return (
    <Chip
      label={disabled ? "Disabled" : "Active"}
      color={disabled ? "error" : "success"}
      size="small"
    />
  );
}

/** Reseller accounts page component. */
export default function ResellerAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferError, setTransferError] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const notify = useNotify();
  const [createForm, setCreateForm] = useState({
    account: "",
    customer_type: "wholesale" as "wholesale" | "pin_test",
    company: "",
    credit_limit: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  /** Self-service downstream account creation (zero balance; fund via Transfer). */
  const handleCreate = async () => {
    if (!createForm.account.trim()) return;
    setCreating(true);
    setCreateError("");
    try {
      const payload: Record<string, unknown> = {
        account: createForm.account.trim(),
        customer_type: createForm.customer_type,
      };
      if (createForm.customer_type === "wholesale") {
        payload.account_type = "*postpaid";
        payload.credit_limit = createForm.credit_limit === "" ? null : parseFloat(createForm.credit_limit);
        payload.company = createForm.company || null;
      }
      const response = await resellerFetch(`${API_BASE_URL}/api/v1/resellers/me/accounts`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Failed to create account");
      }
      setCreateOpen(false);
      setCreateForm({ account: "", customer_type: "wholesale", company: "", credit_limit: "" });
      await fetchAccounts();
    } catch (err: any) {
      setCreateError(err.message || "Failed to create account");
    } finally {
      setCreating(false);
    }
  };

  /** Fetch downstream accounts from API. */
  const fetchAccounts = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("reseller_token");
      const response = await fetch(`${API_BASE_URL}/api/v1/resellers/me/accounts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load accounts");
      }

      const data = await response.json();
      setAccounts(data.resellers || []);
    } catch (err: any) {
      setError(err.message || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  /** Table columns definition. */
  const columns = useMemo(
    () => [
      columnHelper.accessor("level", {
        header: "Level",
        cell: (info) => <LevelChip level={info.getValue()} />,
        enableSorting: true,
      }),
      columnHelper.accessor("account", {
        header: "Account",
        cell: (info) => {
          const level = info.row.original.level;
          const indent = level ? (level - 1) * 20 : 0;
          return (
            <Box sx={{ pl: indent }}>
              <Typography variant="body2" fontWeight="medium">
                {info.getValue()}
              </Typography>
            </Box>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor("full_account_path", {
        header: "Full Path",
        cell: (info) => (
          <Typography variant="caption" color="text.secondary">
            {info.getValue()}
          </Typography>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor("margin_percent", {
        header: "Margin",
        cell: (info) => {
          const margin = info.row.original.margin_percent;
          return margin ? `${margin}%` : "-";
        },
        enableSorting: true,
      }),
      columnHelper.accessor("balance", {
        header: "Balance",
        cell: (info) => `$${info.getValue().toFixed(2)}`,
        enableSorting: true,
      }),
      columnHelper.accessor("disabled", {
        header: "Status",
        cell: (info) => <StatusChip disabled={info.getValue()} />,
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.id, {
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const account = accounts[info.row.index];
          const canTransfer = account.balance > 0 && !account.disabled;

          return (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              <Tooltip title="View CDRs">
                <IconButton
                  size="small"
                  onClick={() => handleViewCdrs(account)}
                  color="info"
                 aria-label="View cdrs">
                  <ViewCdrsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={canTransfer ? "Transfer Balance" : "Insufficient balance or account disabled"}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTransferBalance(account)}
                    color="success"
                    disabled={!canTransfer}
                   aria-label="Transfer balance">
                    <TransferBalanceIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
        enableSorting: false,
      }),
    ],
    [accounts]
  );

  /** Table instance with sorting and filtering. */
  const table = useReactTable({
    data: accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });

  /** Handle view CDRs. */
  const handleViewCdrs = (account: Account) => {
    // Navigate to the CDRs page (deep-linking to a prefiltered view is a
    // follow-up once filters sync to the URL — UX review A4).
    navigate("/cdrs");
  };

  /** Handle transfer balance. */
  const handleTransferBalance = (account: Account) => {
    setSelectedAccount(account);
    setTransferAmount("");
    setTransferError("");
    setTransferDialogOpen(true);
  };

  /** Handle transfer submit. */
  const handleTransferSubmit = async () => {
    if (!selectedAccount) return;

    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      setTransferError("Please enter a valid amount");
      return;
    }

    setTransferring(true);
    setTransferError("");

    try {
      const resellerId = localStorage.getItem("reseller_id");

      // Wave 1: use resellerFetch so the X-CSRF-Token header is attached.
      // The previous raw fetch() omitted it and was rejected 403 by CSRFMiddleware.
      const response = await resellerFetch(
        `${API_BASE_URL}/api/v1/resellers/${resellerId}/transfer-balance`,
        {
          method: "POST",
          body: JSON.stringify({
            destination_account_id: selectedAccount.id,
            amount: amount,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Transfer failed");
      }

      // Close dialog and refresh accounts
      setTransferDialogOpen(false);
      setSelectedAccount(null);
      await fetchAccounts();
    } catch (err: any) {
      setTransferError(err.message || "Failed to transfer balance");
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">My Accounts</Typography>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setImportOpen(true)}
        >
          Import
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        This page shows only your downstream accounts. You cannot view or modify upstream or sibling accounts.
        New accounts start with zero balance — use Transfer to fund them.
      </Alert>

      {accounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No downstream accounts yet — click "Create Account" to add your first
            wholesale client or PIN customer.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sortDirection={
                        header.column.getIsSorted()
                          ? (header.column.getIsSorted() as "asc" | "desc")
                          : false
                      }
                      sx={{ fontWeight: "bold" }}
                    >
                      {header.column.getCanSort() ? (
                        <TableSortLabel
                          active={header.column.getIsSorted() !== false}
                          direction={header.column.getIsSorted() === "asc" ? "asc" : "desc"}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableSortLabel>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  hover
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Transfer balance dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Balance to {selectedAccount?.account}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              disabled={transferring}
            />
            {transferError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {transferError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)} disabled={transferring}>
            Cancel
          </Button>
          <Button
            onClick={handleTransferSubmit}
            variant="contained"
            disabled={transferring}
          >
            {transferring ? <CircularProgress size={24} /> : "Transfer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk import (reseller-owned rows) */}
      <ImportAccountsDialog
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          fetchAccounts();
        }}
        endpoint="/api/v1/resellers/me/import-accounts"
        post={async (ep, body) => {
          const res = await resellerFetch(ep, { method: "POST", body: JSON.stringify(body) });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || "Import failed");
          return data;
        }}
        onImported={(created, failed) => notify(`Import: ${created} created, ${failed} failed`)}
      />

      {/* Create downstream account dialog */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Downstream Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}
            <TextField
              select
              label="Account Type"
              fullWidth
              value={createForm.customer_type}
              onChange={(e) => setCreateForm({ ...createForm, customer_type: e.target.value as "wholesale" | "pin_test" })}
            >
              <MenuItem value="wholesale">Wholesale client (postpaid, IP-authenticated)</MenuItem>
              <MenuItem value="pin_test">PIN customer (prepaid)</MenuItem>
            </TextField>
            <TextField
              label={createForm.customer_type === "wholesale" ? "Account Name" : "PIN"}
              required
              fullWidth
              value={createForm.account}
              onChange={(e) => setCreateForm({ ...createForm, account: e.target.value })}
              helperText="Created with zero balance — fund it via Transfer"
            />
            {createForm.customer_type === "wholesale" && (
              <>
                <TextField
                  label="Company Name"
                  fullWidth
                  value={createForm.company}
                  onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                />
                <TextField
                  label="Credit Limit"
                  fullWidth
                  type="number"
                  value={createForm.credit_limit}
                  onChange={(e) => setCreateForm({ ...createForm, credit_limit: e.target.value })}
                  InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!createForm.account.trim() || creating}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
